/**
 * Data behind the spellbook's three tabs (DESIGN 6.7): the 44 worked
 * examples of DESIGN 3 as cards, the 10 magicks of 2.9 with their key
 * sequences, and the status matrix of 2.7. Pure: the rules-engine calls
 * are injected so the assembly is testable without the engine and keeps
 * working (minus the per-mode strip) if a call throws.
 *
 * The point of the Combos tab is the pillar "the queue is the game": the
 * same queue does a different, visible thing in each cast mode. Every card
 * therefore carries an "across modes" strip (resolveCast for all four
 * modes) next to the worked example, so a player reading "Q F -> Steam
 * Spray" also sees the nova, the self-scald and the thrust the same two
 * keys would give.
 */

import type { BaseElement, CastMode, Element, MagickId, StatusId } from '@shared/index.js';
import { WOVEN_SHORT } from './wovenLabels.js';
import {
  CAST_MODES, ELEMENT_KEYS, MAGICKS, MAGICK_IDS, STATUS_DURATION, pushElement, resolveCast, spellName,
} from '@shared/index.js';

export type ComboMode = CastMode | 'queue' | 'magick';

export interface ComboExample {
  n: number;
  /** Space-separated key tokens: single keys, "QF"/"QR" pairs, "then" for a pause, "G" for the magick key. */
  keys: string;
  mode: ComboMode;
  result: string;
  /** Precondition shown before the keys (row 40). */
  note?: string;
}

/** DESIGN 3, verbatim where the table is, clarified where the table clarifies itself (row 19). */
export const COMBO_EXAMPLES: readonly ComboExample[] = [
  { n: 1, keys: 'S', mode: 'force', result: 'Arcane beam, 11 ticks x 106 = 1166' },
  { n: 2, keys: 'S S S S S', mode: 'force', result: 'Arcane beam, 43 ticks x 106 = 4558' },
  { n: 3, keys: 'S QF QF A', mode: 'force', result: 'Steam-lightning arcane beam: 106 + 90 x 1.41 + 50 = 283/tick (566 vs wet), 11 ticks, wets and shocks the target' },
  { n: 4, keys: 'F', mode: 'force', result: 'Fire spray, 8 ticks x 15, burning' },
  { n: 5, keys: 'F F F F F', mode: 'force', result: 'Fire spray, 16 ticks x 33.5, 5 burn stacks' },
  { n: 6, keys: 'QF QF QF QF QF', mode: 'force', result: 'Steam spray, 16 ticks x 156.5 = 2504, wet' },
  { n: 7, keys: 'QF QF QF QF A', mode: 'force', result: 'Steam spray doubled by lightning: 16 x 140 x 2 = 4480, wets and shocks' },
  { n: 8, keys: 'A', mode: 'force', result: 'Lightning arc, 8 ticks x 31, chains to 2 more targets (allies too), x2 on wet targets' },
  { n: 9, keys: 'A F', mode: 'force', result: 'Lightning arc that ignites: 31/tick + 1 burn stack per tick' },
  { n: 10, keys: 'Q F A', mode: 'force', result: 'Steam spray with lightning: Q F makes steam, then A. Steam outranks lightning; the Water is consumed' },
  { n: 11, keys: 'D', mode: 'force', result: 'Boulder 75 direct (330 at full charge), knockdown' },
  { n: 12, keys: 'D D D D D', mode: 'force', result: 'Boulder 1125 direct, 4950 at full charge' },
  { n: 13, keys: 'D F', mode: 'force', result: 'Fireball: boulder 75 + blast r1.5 with 60 fire + burning to everyone in the radius' },
  { n: 14, keys: 'D S QF QF F', mode: 'force', result: 'Arcane steamball: 75 direct + blast r2.5 with 425 arcane + 396 steam + 60 fire' },
  { n: 15, keys: 'QR', mode: 'force', result: 'Ice barrage: 3 shards x 40, pierce, chilled' },
  { n: 16, keys: 'QR QR QR QR QR', mode: 'force', result: '15 shards x 40, narrow cone when charged' },
  { n: 17, keys: 'D QR QR QR QR', mode: 'force', result: 'Hailstone boulder: 75 + 137.5 x 16 = 2275 direct (Earth outranks Ice)' },
  { n: 18, keys: 'Q F then R', mode: 'queue', result: 'Steam degrades to Water: queue = [water]; a second R makes Ice' },
  { n: 19, keys: 'Q R A F', mode: 'queue', result: 'Q R = [ice]; A = [ice, lightning]; F thaws the Ice = [water, lightning], the only legal Water + Lightning queue' },
  { n: 20, keys: 'F then R', mode: 'queue', result: 'Cancel: queue empty' },
  { n: 21, keys: 'D D then A', mode: 'queue', result: 'One Earth removed, the Lightning discarded: queue = [earth]' },
  { n: 22, keys: 'E then E', mode: 'queue', result: 'Shield cancels itself: queue empty' },
  { n: 23, keys: 'A', mode: 'area', result: 'Lightning nova r3: 250 to everyone in radius except the caster, shocked' },
  { n: 24, keys: 'S R A', mode: 'area', result: 'Arcane-cold-lightning nova r4.5: 425 + 20 + 250 = 695 (945 vs wet), chills then shocks' },
  { n: 25, keys: 'W', mode: 'area', result: 'Life nova: heals 359 to every ally AND enemy in r3' },
  { n: 26, keys: 'D D D', mode: 'area', result: 'Quake r6: knockdown for everyone but the caster, 0 damage' },
  { n: 27, keys: 'QR', mode: 'area', result: 'Ice spikes nova r3: 275, chilled (QR is Ice, not Water + Cold)' },
  { n: 28, keys: 'E', mode: 'force', result: 'Barrier arc 120 deg, 500 HP, blocks beams' },
  { n: 29, keys: 'E S', mode: 'area', result: '8 arcane mines in a ring: 225 + launch 10 u/s each; anyone triggers them' },
  { n: 30, keys: 'E W', mode: 'force', result: '4 healing mines in an arc, 599 heal' },
  { n: 31, keys: 'E D F', mode: 'force', result: 'Volcano wall: stone wall 2000 HP, 20 s; the rise knocks down + 60 fire + burning' },
  { n: 32, keys: 'E QR A', mode: 'force', result: 'Tesla wall: ice wall 800 HP, 6 s; the rise deals 275 physical + 40 lightning + shock' },
  { n: 33, keys: 'E A', mode: 'area', result: 'Lightning storm ring 10 s: 65 + shock every 0.5 s to anyone inside' },
  { n: 34, keys: 'E F', mode: 'self', result: 'Fire resistance aura r5, 15 s: no fire damage or burning for anyone inside' },
  { n: 35, keys: 'E D', mode: 'self', result: 'Rock armor: absorbs 1500 physical/arcane/lightning, slow 30%, 10 s' },
  { n: 36, keys: 'W', mode: 'self', result: 'Heal 179' },
  { n: 37, keys: 'W W W', mode: 'self', result: 'Heal 179 + 358 over 3 s' },
  { n: 38, keys: 'F', mode: 'self', result: 'Burning 1 stack; cures wet, chilled and frozen' },
  { n: 39, keys: 'D D D D D', mode: 'self', result: '1125 physical to yourself: dead unless armored' },
  { n: 40, keys: 'A', mode: 'queue', note: 'while wet', result: 'Self-shock: 250 damage, queue cleared' },
  { n: 41, keys: 'S F', mode: 'weapon', result: 'Imbued swing: 100 physical + 425 arcane + 60 fire + burning in a 100 deg arc' },
  { n: 42, keys: 'D', mode: 'weapon', result: 'Fissure, 5 u line: knockdown, 0 damage' },
  { n: 43, keys: 'W A G', mode: 'magick', result: 'Revive the nearest dead ally at 100 HP' },
  { n: 44, keys: 'QF A S A G', mode: 'magick', result: 'Thunder Bolt 5000 (10000 on a wet troll = dead)' },
  // New fusions: the second key fuses into the first, so order matters (Q then D is Mud; D then Q is not).
  { n: 45, keys: 'QD', mode: 'force', result: 'Mud spray: soaks AND mires (move x0.45) everything in the cone. Follow with Cold to freeze them in place, or Lightning for double damage' },
  { n: 46, keys: 'QD QD', mode: 'area', result: 'Mudslide: a ring of mud around you, everyone nearby stuck and soaked' },
  { n: 47, keys: 'FD', mode: 'force', result: 'Lava boulder: knocks down, splashes fire, and leaves a burning pool that hurts anyone who stands in it (you too)' },
  { n: 48, keys: 'FD FD', mode: 'area', result: 'Eruption: a quake that knocks everyone down and sets them alight' },
  { n: 49, keys: 'E FD', mode: 'force', result: 'Volcano wall: a stone wall that burns whoever it rises under' },
  { n: 50, keys: 'QW', mode: 'force', result: 'Healing mist: heals what it touches (enemies too) and puts out fires, but leaves everyone wet' },
  { n: 51, keys: 'QW', mode: 'self', result: 'Spring bath: heal yourself and douse flames, at the cost of being wet' },
  { n: 52, keys: 'E QD', mode: 'force', result: 'Swamp: a band of mud that mires and soaks anything walking through' },
  { n: 53, keys: 'QD then F', mode: 'queue', result: 'Fire bakes Mud back into Earth: queue = [earth]. Cold or Water cools Lava the same way' },
];

/** Mode headings with the input that triggers them (DESIGN 6.1). */
export const COMBO_MODE_LABELS: Readonly<Record<ComboMode, string>> = {
  force: 'Force: hold LMB',
  area: 'Area: Shift + LMB',
  self: 'Self: middle click',
  weapon: 'Weapon: V',
  queue: 'Queue tricks',
  magick: 'Magick: Space (or G)',
};

export const COMBO_MODE_ORDER: readonly ComboMode[] = ['force', 'area', 'self', 'weapon', 'queue', 'magick'];

// ------------------------------------------------------------------ key tokens

export type KeyToken =
  | { kind: 'el'; label: string; el: Element; presses: BaseElement[] }
  | { kind: 'then' }
  | { kind: 'magick' }
  | { kind: 'text'; label: string };

/** Key letter -> element, from the physical key table (KeyQ -> 'Q'). */
export const KEY_ELEMENT: Readonly<Record<string, BaseElement>> = Object.fromEntries(
  Object.entries(ELEMENT_KEYS).map(([code, el]) => [code.replace(/^Key/, ''), el]),
);

/** Element -> key label; steam and ice are the two-key combos they come from. */
export const ELEMENT_KEY: Readonly<Record<Element, string>> = {
  ...(Object.fromEntries(Object.entries(KEY_ELEMENT).map(([k, el]) => [el, k])) as Record<BaseElement, string>),
  steam: 'QF',
  ice: 'QR',
  mud: 'QD',
  lava: 'FD',
  spring: 'QW',
  ...WOVEN_SHORT,
};

/** "S QF then A G" -> tokens; "QF" reads as steam, "QR" as ice, anything unknown is shown as text. */
export function tokenizeKeys(keys: string): KeyToken[] {
  const out: KeyToken[] = [];
  for (const raw of keys.split(/\s+/)) {
    if (raw === '') continue;
    const t = raw.toUpperCase();
    if (t === 'THEN') { out.push({ kind: 'then' }); continue; }
    if (t === 'G') { out.push({ kind: 'magick' }); continue; }
    if (t === 'QF') { out.push({ kind: 'el', label: t, el: 'steam', presses: ['water', 'fire'] }); continue; }
    if (t === 'QR') { out.push({ kind: 'el', label: t, el: 'ice', presses: ['water', 'cold'] }); continue; }
    if (t === 'QD') { out.push({ kind: 'el', label: t, el: 'mud', presses: ['water', 'earth'] }); continue; }
    if (t === 'FD') { out.push({ kind: 'el', label: t, el: 'lava', presses: ['fire', 'earth'] }); continue; }
    if (t === 'QW') { out.push({ kind: 'el', label: t, el: 'spring', presses: ['water', 'life'] }); continue; }
    const el = t.length === 1 ? KEY_ELEMENT[t] : undefined;
    if (el) out.push({ kind: 'el', label: t, el, presses: [el] });
    else out.push({ kind: 'text', label: raw });
  }
  return out;
}

// ------------------------------------------------------------------ engine-backed assembly

/** The three rules-engine calls the cards need; injectable for tests and stub-proofing. */
export interface EngineDeps {
  push: typeof pushElement;
  resolve: typeof resolveCast;
  name: typeof spellName;
}

export const ENGINE: EngineDeps = { push: pushElement, resolve: resolveCast, name: spellName };

/** Run the presses of a key string through the queue algorithm; `wet` = the caster is wet (row 40). */
export function queueFromKeys(keys: string, push: EngineDeps['push'], wet = false): Element[] {
  let queue: Element[] = [];
  for (const tok of tokenizeKeys(keys)) {
    if (tok.kind === 'magick') break;
    if (tok.kind !== 'el') continue;
    for (const press of tok.presses) queue = push(queue, press, wet).queue;
  }
  return queue;
}

export interface ModeLine { mode: CastMode; name: string }

/** What a queue does in each of the four cast modes (empty when the engine is unavailable). */
export function acrossModes(queue: readonly Element[], deps: EngineDeps | null): ModeLine[] {
  if (!deps || queue.length === 0) return [];
  try {
    return CAST_MODES.map((mode) => ({ mode, name: deps.name(deps.resolve(queue, mode)) }));
  } catch {
    return [];
  }
}

export interface ComboCard {
  n: number;
  tokens: KeyToken[];
  mode: ComboMode;
  modeLabel: string;
  note: string | null;
  result: string;
  /** The queue the keys build (what the chips under the keys show). */
  queue: Element[];
  across: ModeLine[];
}

export function assembleCombos(examples: readonly ComboExample[] = COMBO_EXAMPLES, deps: EngineDeps | null = ENGINE): ComboCard[] {
  return examples.map((ex) => {
    let queue: Element[] = [];
    if (deps) {
      try {
        queue = queueFromKeys(ex.keys, deps.push, ex.note === 'while wet');
      } catch {
        queue = [];
      }
    }
    return {
      n: ex.n,
      tokens: tokenizeKeys(ex.keys),
      mode: ex.mode,
      modeLabel: COMBO_MODE_LABELS[ex.mode],
      note: ex.note ?? null,
      result: ex.result,
      queue,
      across: acrossModes(queue, deps),
    };
  });
}

/** Cards grouped by mode in COMBO_MODE_ORDER, each group non-empty. */
export function groupCombos(cards: readonly ComboCard[]): { mode: ComboMode; label: string; cards: ComboCard[] }[] {
  return COMBO_MODE_ORDER
    .map((mode) => ({ mode, label: COMBO_MODE_LABELS[mode], cards: cards.filter((c) => c.mode === mode) }))
    .filter((g) => g.cards.length > 0);
}

// ------------------------------------------------------------------ magicks (DESIGN 2.9)

export interface MagickRow { id: MagickId; name: string; desc: string; seq: Element[]; keys: string }

export function magickRows(): MagickRow[] {
  return MAGICK_IDS.map((id) => {
    const def = MAGICKS[id];
    return { id, name: def.name, desc: def.desc, seq: [...def.seq], keys: def.seq.map((el) => ELEMENT_KEY[el]).join(' ') };
  });
}

// ------------------------------------------------------------------ statuses (DESIGN 2.7)

export interface StatusRow { id: StatusId; name: string; duration: number; effect: string; appliedBy: string; removedBy: string }

const STATUS_TEXT: Readonly<Record<StatusId, Omit<StatusRow, 'id' | 'duration'>>> = {
  wet: { name: 'Wet', effect: 'Lightning damage x2. Pressing A shocks YOU (250, queue cleared). Cold freezes.', appliedBy: 'Water, Steam, Rain, the pond, rain/steam storms', removedBy: 'Burning, fire damage, self-cast Fire, Wind, Frozen (replaces it)' },
  burning: { name: 'Burning', effect: '20 damage/s per stack (max 6 = 120/s). Removes wet, chilled, frozen. Enemies with 4+ stacks panic.', appliedBy: 'Fire (not Steam), bombs', removedBy: 'Wet, chilled, frozen, Wind, water damage' },
  chilled: { name: 'Chilled', effect: '-40% move and cast speed, beam turns at half rate. Removes burning.', appliedBy: 'Cold on a dry target, Ice shards', removedBy: 'Fire, time' },
  frozen: { name: 'Frozen', effect: 'Cannot move or cast (self-casts and Teleport still work). ALL damage x3. Replaces wet.', appliedBy: 'Cold on a wet target, Blizzard on wet', removedBy: 'Fire damage, self-cast Fire, time' },
  shocked: { name: 'Shocked', effect: 'Stunned: no movement, no casting, channels interrupted. Refreshed by every lightning tick.', appliedBy: 'Any lightning damage', removedBy: 'Time' },
  knockdown: { name: 'Knocked down', effect: 'Prone: no movement; only self-casts and Teleport. Immune to knockback meanwhile.', appliedBy: 'Earth boulder, quake, fissure, mine blasts, troll punch', removedBy: 'Time' },
  hasted: { name: 'Hasted', effect: 'Move x2, beam rotation x2.', appliedBy: 'Haste magick', removedBy: 'Nullify, time' },
  slowed: { name: 'Slowed', effect: 'Move and cast x0.5.', appliedBy: 'Time Warp magick', removedBy: 'Nullify, time' },
  blinded: { name: 'Blinded', effect: 'An enemy loses its target and wanders; ranged ones cannot aim. Does nothing to wizards.', appliedBy: 'Ash (Lava + Steam, woven)', removedBy: 'Time' },
  mired: { name: 'Mired', effect: 'Stuck in mud: move x0.45. Mud also soaks, so lightning or cold hits hard next.', appliedBy: 'Mud (Q then D) sprays, novas, boulders and swamp storms', removedBy: 'Burning (dries it), time' },
};

export const STATUS_ORDER: readonly StatusId[] = ['wet', 'burning', 'chilled', 'frozen', 'shocked', 'knockdown', 'mired', 'blinded', 'hasted', 'slowed'];

export function statusRows(): StatusRow[] {
  return STATUS_ORDER.map((id) => ({ id, duration: STATUS_DURATION[id], ...STATUS_TEXT[id] }));
}

/** The incoming statuses a spell can carry (haste/slow only come from magicks). */
export const INCOMING_STATUSES: readonly StatusId[] = ['wet', 'burning', 'chilled', 'shocked', 'knockdown'];

/**
 * Interaction matrix: the target already has the row status, a spell
 * applies the column status. Cells follow status.ts applyStatus; "+" means
 * both stay, "->" means the row status is replaced.
 */
export const STATUS_MATRIX: Readonly<Record<StatusId, Readonly<Record<StatusId, string>>>> = {
  wet: { wet: 'refreshed', burning: '-> burning (dried)', chilled: '-> FROZEN', frozen: '-> frozen', shocked: '+ shocked, x2 damage', knockdown: '+ knocked down', hasted: '+', slowed: '+', mired: '+ mired', blinded: '+' },
  burning: { wet: '-> wet (put out)', burning: '+1 stack', chilled: '-> chilled (put out)', frozen: '-> frozen (put out)', shocked: '+ shocked', knockdown: '+ knocked down', hasted: '+', slowed: '+', mired: '-> burning (dried out)', blinded: '+' },
  chilled: { wet: '+ wet; next Cold freezes', burning: '-> burning (thawed)', chilled: 'refreshed', frozen: '-> frozen', shocked: '+ shocked', knockdown: '+ knocked down', hasted: '+', slowed: '+', mired: '+ mired', blinded: '+' },
  frozen: { wet: 'ignored', burning: '-> burning (thawed)', chilled: 'ignored', frozen: 'refreshed', shocked: '+ shocked, x3 damage', knockdown: '+ knocked down', hasted: '+', slowed: '+', mired: '+ mired', blinded: '+' },
  shocked: { wet: '+ wet', burning: '+ burning', chilled: '+ chilled', frozen: '+ frozen', shocked: 'refreshed', knockdown: '+ knocked down', hasted: '+', slowed: '+', mired: '+ mired', blinded: '+' },
  knockdown: { wet: '+ wet', burning: '+ burning', chilled: '+ chilled', frozen: '+ frozen', shocked: '+ shocked', knockdown: 'refreshed', hasted: '+', slowed: '+', mired: '+ mired', blinded: '+' },
  hasted: { wet: '+', burning: '+', chilled: '+', frozen: '+', shocked: '+', knockdown: '+', hasted: 'refreshed', slowed: '+', mired: '+', blinded: '+' },
  slowed: { wet: '+', burning: '+', chilled: '+', frozen: '+', shocked: '+', knockdown: '+', hasted: '+', slowed: 'refreshed', mired: '+', blinded: '+' },
  mired: { wet: '+ wet', burning: '-> burning (dried out)', chilled: '+ chilled', frozen: '+ frozen', shocked: '+ shocked', knockdown: '+ knocked down', hasted: '+', slowed: '+', mired: 'refreshed', blinded: '+' },
  blinded: { wet: '+', burning: '+', chilled: '+', frozen: '+', shocked: '+', knockdown: '+', hasted: '+', slowed: '+', mired: '+', blinded: 'refreshed' },
};

export type DamageColumn = 'physical' | 'arcane' | 'life' | 'fire' | 'cold' | 'lightning' | 'steam' | 'water';
export const DAMAGE_COLUMNS: readonly DamageColumn[] = ['physical', 'arcane', 'life', 'fire', 'cold', 'lightning', 'steam', 'water'];

export interface MultiplierRow { condition: string; cells: Partial<Record<DamageColumn, string>> }

/** Damage multipliers of DESIGN 2.7 and the enemy traits of 4.3; they multiply together. */
export const MULTIPLIER_ROWS: readonly MultiplierRow[] = [
  { condition: 'Wet', cells: { lightning: 'x2' } },
  { condition: 'Frozen', cells: { physical: 'x3', arcane: 'x3', fire: 'x3', cold: 'x3', lightning: 'x3', steam: 'x3', water: 'x3' } },
  { condition: 'Armored (orc)', cells: { physical: 'x0.5', fire: 'x0', lightning: 'x1.5' } },
  { condition: 'Undead (skeleton)', cells: { life: 'hurts', arcane: 'heals', fire: 'x0.5' } },
  { condition: 'Tree spirit', cells: { fire: 'x2', water: 'never wet' } },
  { condition: 'Jotunn', cells: { lightning: 'x0' } },
];
